import { createClient } from '@supabase/supabase-js';
import gameSessionsApi, { loadCredentials } from './gameSessionsApi';

const RESUBSCRIBE_DELAY_MS = 5000;
const CLIENT_RETRY_DELAY_MS = 30000;
const FAILED_STATUSES = ['CHANNEL_ERROR', 'TIMED_OUT', 'CLOSED'];
// sessionStorage so the Supabase session ends with the tab and never outlives a Rails logout.
const STORAGE_KEY = 'lexody-games-auth';

let clientPromise = null;
let realtimeUnavailable = false;

class RealtimeUnavailableError extends Error {}

async function createRealtimeClient() {
  const creds = await loadCredentials();
  if (!creds.supabase_url || !creds.supabase_anon_key || !creds.auth_user_id) {
    throw new RealtimeUnavailableError('Realtime needs a migrated account');
  }

  const client = createClient(creds.supabase_url, creds.supabase_anon_key, {
    auth: {
      storage: window.sessionStorage,
      storageKey: STORAGE_KEY,
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
    },
  });

  const { data } = await client.auth.getSession();
  let session = data && data.session;
  if (!session || session.user.id !== creds.auth_user_id) {
    if (session) await client.auth.signOut({ scope: 'local' });
    const link = await gameSessionsApi.realtimeLink();
    if (!link || !link.token_hash) {
      throw new RealtimeUnavailableError(`Realtime unavailable (${link ? link.reason : 'no link'})`);
    }
    const { data: verified, error } = await client.auth.verifyOtp({
      token_hash: link.token_hash,
      type: 'magiclink',
    });
    if (error || !verified || !verified.session) {
      throw error || new Error('No Supabase session returned');
    }
    session = verified.session;
  }

  await client.realtime.setAuth(session.access_token);
  client.auth.onAuthStateChange((event, next) => {
    if (next && next.access_token) client.realtime.setAuth(next.access_token);
  });
  return client;
}

function getClient() {
  if (!clientPromise) {
    clientPromise = createRealtimeClient().catch((error) => {
      clientPromise = null;
      if (error instanceof RealtimeUnavailableError) realtimeUnavailable = true;
      throw error;
    });
  }
  return clientPromise;
}

/**
 * Watches one game_sessions row over Supabase Realtime. The row is only a change
 * signal — callers re-GET the role-aware snapshot from the API.
 *
 * When Realtime can't be used (account not on Supabase Auth yet, network trouble),
 * `onStatus(false)` fires and the caller should poll. Returns a stop function.
 */
export function watchGameSession(sessionId, { onChange, onStatus }) {
  let stopped = false;
  let client = null;
  let channel = null;
  let retryTimer = null;

  const scheduleRetry = (delay) => {
    if (stopped || retryTimer || realtimeUnavailable) return;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      subscribe();
    }, delay);
  };

  const subscribe = async () => {
    try {
      client = await getClient();
    } catch (error) {
      if (stopped) return;
      console.log('[GameRealtime]', error.message);
      onStatus && onStatus(false);
      scheduleRetry(CLIENT_RETRY_DELAY_MS);
      return;
    }
    if (stopped) return;

    if (channel) {
      const old = channel;
      channel = null;
      client.removeChannel(old);
    }

    const current = client
      .channel(`game_session_${sessionId}_${Date.now()}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'game_sessions', filter: `id=eq.${sessionId}` },
        (payload) => onChange && onChange(payload.new)
      )
      .subscribe((status) => {
        if (stopped || channel !== current) return;
        if (status === 'SUBSCRIBED') {
          onStatus && onStatus(true);
        } else if (FAILED_STATUSES.includes(status)) {
          onStatus && onStatus(false);
          scheduleRetry(RESUBSCRIBE_DELAY_MS);
        }
      });
    channel = current;
  };

  subscribe();

  return () => {
    stopped = true;
    clearTimeout(retryTimer);
    if (channel && client) {
      client.removeChannel(channel);
      channel = null;
    }
  };
}
